import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import Sidebar from '@/components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FileText, Plus, Send, Copy, Trash2, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';

const STATUS_COLORS = {
  draft: 'bg-slate-200 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-rose-100 text-rose-700',
};

export default function Proposals() {
  const { user } = useAuth();
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [quotes, setQuotes] = useState([]);

  const canManage = canEdit(user, 'proposals');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, eRes, qRes] = await Promise.all([
        api.get('/proposals'),
        api.get('/clients'),
        api.get('/events'),
        api.get('/quotes'),
      ]);
      setProposals(pRes.data || []);
      setClients(cRes.data || []);
      setEvents(eRes.data || []);
      setQuotes(qRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (proposalId) {
    return <ProposalEditor proposalId={proposalId} clients={clients} events={events} quotes={quotes} canManage={canManage} onClose={() => { navigate('/proposals'); load(); }} />;
  }

  const create = async () => {
    if (clients.length === 0) {
      toast.error('Create a client first');
      navigate('/clients');
      return;
    }
    try {
      const r = await api.post('/proposals', { client_id: clients[0].id, title: 'New proposal' });
      navigate(`/proposals/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <FileText className="w-7 h-7 text-emerald-600" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Proposals</h1>
                <p className="text-sm text-slate-500">Long-form proposals for clients — scope, attachments, and a public accept link.</p>
              </div>
            </div>
            {canManage && <Button onClick={create}><Plus className="w-4 h-4 mr-1" /> New proposal</Button>}
          </div>

          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading…</p>
          ) : proposals.length === 0 ? (
            <Card className="glass-card p-12 text-center text-slate-500"><FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />No proposals yet.</Card>
          ) : (
            <Card className="glass-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b text-xs text-slate-600 text-left">
                  <tr><th className="px-4 py-2">Number</th><th className="px-4 py-2">Title</th><th className="px-4 py-2">Client</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Created</th><th></th></tr>
                </thead>
                <tbody>
                  {proposals.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs">{p.number}</td>
                      <td className="px-4 py-2">{p.title}</td>
                      <td className="px-4 py-2">{clients.find((c) => c.id === p.client_id)?.name || '—'}</td>
                      <td className="px-4 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[p.status] || 'bg-slate-100'}`}>{p.status}</span></td>
                      <td className="px-4 py-2 text-xs text-slate-500">{p.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-2 text-right"><Link to={`/proposals/${p.id}`} className="text-emerald-600 text-xs">Open →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

function ProposalEditor({ proposalId, clients, events, quotes, canManage, onClose }) {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/proposals/${proposalId}`);
      setProposal(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [proposalId, onClose]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setProposal((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/proposals/${proposalId}`, {
        title: proposal.title,
        body_html: proposal.body_html,
        cover_image_url: proposal.cover_image_url,
        attachments: proposal.attachments,
        quote_id: proposal.quote_id,
        client_id: proposal.client_id,
      });
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!window.confirm('Mark as sent and copy share link?')) return;
    try {
      const r = await api.post(`/proposals/${proposalId}/send`);
      setProposal(r.data);
      const link = `${window.location.origin}${r.data.share_url}`;
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Sent · link copied');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    }
  };

  const copyLink = () => {
    if (!proposal.public_token) return;
    const link = `${window.location.origin}/share/proposal/${proposal.public_token}`;
    navigator.clipboard.writeText(link).catch(() => {});
    toast.success('Link copied');
  };

  const remove = async () => {
    if (!window.confirm('Delete this proposal?')) return;
    try {
      await api.delete(`/proposals/${proposalId}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  if (loading || !proposal) return <div className="min-h-screen bg-slate-50"><Sidebar /><main className="md:ml-64 p-8"><p>Loading…</p></main></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <Button variant="ghost" size="sm" onClick={onClose}>← Back</Button>
              <h1 className="text-2xl font-bold mt-2">Proposal <span className="font-mono text-emerald-600">{proposal.number}</span>
                <span className={`ml-3 text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[proposal.status] || 'bg-slate-100'}`}>{proposal.status}</span>
              </h1>
            </div>
            {canManage && (
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
                {proposal.status === 'draft' && <Button size="sm" onClick={send}><Send className="w-3.5 h-3.5 mr-1" /> Mark sent</Button>}
                {proposal.public_token && <Button variant="outline" size="sm" onClick={copyLink}><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>}
                <Button variant="ghost" size="sm" onClick={remove} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </div>

          <Card className="glass-card p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <Label>Title</Label>
                <Input value={proposal.title || ''} onChange={(e) => update({ title: e.target.value })} />
              </div>
              <div>
                <Label>Client</Label>
                <Select value={proposal.client_id} onValueChange={(v) => update({ client_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event</Label>
                <Select value={proposal.event_id || ''} onValueChange={(v) => update({ event_id: v || null })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="">— None —</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Linked quote</Label>
                <Select value={proposal.quote_id || ''} onValueChange={(v) => update({ quote_id: v || null })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="">— None —</SelectItem>{quotes.map((q) => <SelectItem key={q.id} value={q.id}>{q.number} · {q.title || 'Untitled'}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label>Cover image URL</Label>
                <Input value={proposal.cover_image_url || ''} onChange={(e) => update({ cover_image_url: e.target.value })} placeholder="https://…" />
              </div>
              <div className="md:col-span-3">
                <Label>Body (HTML allowed)</Label>
                <textarea
                  rows={14}
                  value={proposal.body_html || ''}
                  onChange={(e) => update({ body_html: e.target.value })}
                  className="w-full border rounded p-2 text-sm font-mono"
                  placeholder="<h2>Project scope</h2>..."
                />
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
